import { useState, useEffect } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';

import { useAppContext } from '../../../utils/content.util';
import { onError } from '../../../utils/error.util';
import { DealerProspect } from '../../../models/dealer-prospect.model';
import { getMyDealerProspects } from '../../../services/dealer-prospects.service';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import ProspectsPageBar from '../../../components/admin/prospects-page-bar/prospects-page-bar.component';
import ProspectsTable from '../../../components/admin/prospects-table/prospects-table.component';
import { CompanyConfig } from '../../../company.config';

const LIMIT = 100;

/** Slugify for URL path: lowercase, spaces to hyphens, keep alphanumeric and hyphens */
function slugify(value: string | null | undefined): string {
  return (value ?? '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/** My Leads form URL: no dealer in path (dealer is optional and auto-populates on Slide 5 if chosen) */
function buildMyLeadFormUrl(leadName: string): string {
  const base = CompanyConfig.leadFormBaseUrl ?? 'https://get-started.trailersolutionsfl.com';
  const leadSlug = slugify(leadName) || 'lead';
  return `${base}/lead/name/${leadSlug}`;
}

export default function MyLeadsContainer() {
  const { isAuthenticated } = useAppContext();
  const [leads, setLeads] = useState<DealerProspect[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySnackbarOpen(true);
    } catch (e) {
      onError(e as Error);
    }
  };

  async function loadLeads() {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    setLeads([]);
    setIsLoading(true);
    try {
      const response = await getMyDealerProspects({ limit: LIMIT });
      const items = (response.items ?? []) as DealerProspect[];
      setLeads(items);
    } catch (e) {
      onError(e as Error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleSearch = (query: string) => setSearchQuery(query.trim().toLowerCase());
  const handleSearchClear = () => setSearchQuery('');

  const filteredLeads = searchQuery
    ? leads.filter(
        (row) =>
          (row.leadTypeName?.toLowerCase() ?? '').includes(searchQuery) ||
          (row.dealershipName?.toLowerCase() ?? '').includes(searchQuery)
      )
    : leads;

  return (
    <Grid size={{ xs: 12 }}>
      <ProspectsPageBar
        title="My Leads"
        createButton={
          <ReactRouterLink to="/dealer-prospects/new">
            <Button variant="contained">Create Lead Form</Button>
          </ReactRouterLink>
        }
        onSearch={handleSearch}
        onSearchClear={handleSearchClear}
      />

      {isLoading ? (
        <CircularProgressComponent />
      ) : (
        <ProspectsTable
          leads={filteredLeads}
          getCopyUrl={(row) => buildMyLeadFormUrl(row.leadTypeName)}
          onCopyLink={handleCopyLink}
        />
      )}

      {!isLoading && filteredLeads.length === 0 && isAuthenticated ? (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {searchQuery
            ? 'No matching leads.'
            : 'No lead forms yet. Use the Create Lead Form button above to add one.'}
        </Typography>
      ) : null}

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Grid>
  );
}
